import { debugLog } from "@/helpers/debug-log-helper.js";
import { endorsementTypes } from "@/constants/endorsements";
import { hasEndorsementType } from "@/helpers/endorsements-helper";

export function isUsableVin(vin) {
    return normalizeVehicleToken(vin).length >= 11;
}

export function policyVehicleHasEndorsements(policyVehicle) {
    const endorsements = policyVehicle?.endorsements;
    return (
        hasEndorsementType(endorsements, endorsementTypes.EDUCATOR) ||
        hasEndorsementType(endorsements, endorsementTypes.PARKING_GUARD)
    );
}

export function getPolicyVehicleDisplayLabel(policyVehicle) {
    return formatVehicleYmmsLabel(
        policyVehicle?.vehicleYear,
        policyVehicle?.vehicleMake,
        policyVehicle?.vehicleModel
    );
}

export function getEnteredVehicleDisplayLabel(vehicle) {
    return formatVehicleYmmsLabel(vehicle?.year, vehicle?.make, vehicle?.model);
}

export function formatMaskedVin(vin) {
    const normalized = normalizeVehicleToken(vin);
    if (normalized.length < 11) {
        return "";
    }

    return `VIN: <span class="policy-vehicle-vin-mask">••••••</span>${escapeHtml(
        normalized.slice(-6)
    )}`;
}

export function formatVehicleYmmsLabel(year, make, model) {
    return [year, make, model].filter(Boolean).join(" ").toUpperCase();
}

export function escapeHtml(value) {
    return (value ?? "")
        .toString()
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

export function doVinsMatch(leftVin, rightVin) {
    return (
        isUsableVin(leftVin) &&
        isUsableVin(rightVin) &&
        normalizeVehicleToken(leftVin) === normalizeVehicleToken(rightVin)
    );
}

// policyVehicleCarIds maps policy vehicle id => car id decoded from that vehicle's VIN.
function doCarIdsMatch(policyVehicle, enteredVehicle, policyVehicleCarIds) {
    const policyCarId = policyVehicleCarIds?.[policyVehicle?.id?.toString()];
    const enteredCarId = enteredVehicle?.carId;
    return !!policyCarId && !!enteredCarId && policyCarId.toString() === enteredCarId.toString();
}

export function doesPolicyVehicleMatchEnteredVehicle(
    policyVehicle,
    enteredVehicle,
    policyVehicleCarIds = {}
) {
    if (!policyVehicle || !enteredVehicle) {
        debugLog("policyVehicle or enteredVehicle is null");
        return false;
    }

    debugLog("policyVin", normalizeVehicleToken(policyVehicle.vin));
    debugLog("enteredVin", normalizeVehicleToken(enteredVehicle.vin));
    if (doVinsMatch(policyVehicle.vin, enteredVehicle.vin)) {
        // prettier-ignore
        debugLog(`vin match: policy vehicle vin: ${policyVehicle.vin} and entered vehicle vin: ${enteredVehicle.vin}`);
        return true;
    }

    const carIdMatch = doCarIdsMatch(policyVehicle, enteredVehicle, policyVehicleCarIds);
    if (carIdMatch) {
        // prettier-ignore
        debugLog(`carId match: policy vehicle car id: ${policyVehicleCarIds?.[policyVehicle.id?.toString()]} and entered vehicle carId: ${enteredVehicle.carId}`);
        return true;
    }

    // if we have vins but didn't match above then no match applied for preselection
    if (isUsableVin(policyVehicle.vin) && isUsableVin(enteredVehicle.vin)) {
        debugLog("no match applied for preselection");
        return false;
    }

    const yearMatch = yearsMatch(policyVehicle.vehicleYear, enteredVehicle.year);
    const makeMatch = tokensMatch(policyVehicle.vehicleMake, enteredVehicle.make);
    const modelMatch = tokensMatch(policyVehicle.vehicleModel, enteredVehicle.model);

    // prettier-ignore
    {
        debugLog(`year ${yearMatch ? "match" : "mismatch"}: policy vehicle year: ${policyVehicle.vehicleYear} and entered vehicle year: ${enteredVehicle.year}`);
        debugLog(`make ${makeMatch ? "match" : "mismatch"}: policy vehicle make: ${policyVehicle.vehicleMake} and entered vehicle make: ${enteredVehicle.make}`);
        debugLog(`model ${modelMatch ? "match" : "mismatch"}: policy vehicle model: ${policyVehicle.vehicleModel} and entered vehicle model: ${enteredVehicle.model}`);

        const isYmmMatch = yearMatch && makeMatch && modelMatch;
        debugLog(`year/make/model ${isYmmMatch ? "match" : "mismatch"}: policy vehicle year: ${policyVehicle.vehicleYear} and entered vehicle year: ${enteredVehicle.year}, policy vehicle make: ${policyVehicle.vehicleMake} and entered vehicle make: ${enteredVehicle.make}, policy vehicle model: ${policyVehicle.vehicleModel} and entered vehicle model: ${enteredVehicle.model}`);
        return isYmmMatch;
    }
}

// Prefers an exact VIN match, then a car id match, then year/make/model, so a second
// vehicle with the same car id or similar name can't win over the customer's own VIN.
export function findMatchingPolicyVehicle(
    policyVehicles,
    enteredVehicle,
    policyVehicleCarIds = {}
) {
    if (!Array.isArray(policyVehicles) || !enteredVehicle) {
        return null;
    }

    const vinMatch = policyVehicles.find((policyVehicle) =>
        doVinsMatch(policyVehicle?.vin, enteredVehicle.vin)
    );
    if (vinMatch) {
        // prettier-ignore
        debugLog(`vin match: policy vehicle vin: ${vinMatch.vin} and entered vehicle vin: ${enteredVehicle.vin}`);
        return vinMatch;
    }

    const carIdMatch = policyVehicles.find((policyVehicle) =>
        doCarIdsMatch(policyVehicle, enteredVehicle, policyVehicleCarIds)
    );
    if (carIdMatch) {
        // prettier-ignore
        debugLog(`carId match: policy vehicle car id: ${policyVehicleCarIds?.[carIdMatch.id?.toString()]} and entered vehicle carId: ${enteredVehicle.carId}`);
        return carIdMatch;
    }

    const ymmMatch = policyVehicles.find((policyVehicle) =>
        doesPolicyVehicleMatchEnteredVehicle(policyVehicle, enteredVehicle, policyVehicleCarIds)
    );

    if (!ymmMatch) {
        // prettier-ignore
        debugLog(`no policy vehicle matched: entered vehicle vin: ${enteredVehicle.vin}, carId: ${enteredVehicle.carId}, year: ${enteredVehicle.year}, make: ${enteredVehicle.make}, model: ${enteredVehicle.model}`);
        return null;
    }

    // prettier-ignore
    debugLog(`year/make/model match: policy vehicle year: ${ymmMatch.vehicleYear} and entered vehicle year: ${enteredVehicle.year}, policy vehicle make: ${ymmMatch.vehicleMake} and entered vehicle make: ${enteredVehicle.make}, policy vehicle model: ${ymmMatch.vehicleModel} and entered vehicle model: ${enteredVehicle.model}`);
    return ymmMatch;
}

export function getSelectedPolicyVehicle(policyVehicles, policyVehicleId) {
    if (!Array.isArray(policyVehicles) || policyVehicleId == null) {
        return null;
    }

    return (
        policyVehicles.find(
            (policyVehicle) => policyVehicle?.id?.toString() === policyVehicleId?.toString()
        ) ?? null
    );
}

function normalizeVehicleToken(value) {
    return (value ?? "")
        .toString()
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "");
}

// Years must match exactly. tokensMatch's starts-with check would match a 2-digit year to a 4-digit one.
function yearsMatch(left, right) {
    const normalizedLeft = normalizeVehicleToken(left);
    const normalizedRight = normalizeVehicleToken(right);

    if (!normalizedLeft || !normalizedRight) {
        return false;
    }

    return normalizedLeft === normalizedRight;
}

function tokensMatch(left, right) {
    const normalizedLeft = normalizeVehicleToken(left);
    const normalizedRight = normalizeVehicleToken(right);
    if (!normalizedLeft || !normalizedRight) {
        return false;
    }

    if (normalizedLeft === normalizedRight) {
        return true;
    }

    return normalizedLeft.startsWith(normalizedRight) || normalizedRight.startsWith(normalizedLeft);
}
